import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { t } from '../../lib/i18n';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import {
  Search,
  MoreVertical,
  Shield,
  ShieldOff,
  UserCheck,
  UserX,
  KeyRound,
  Trash2,
  ChevronLeft,
  ChevronRight,
  UserPlus,
  Pencil,
  Download,
} from 'lucide-react';

const PAGE_SIZE = 20;

const AdminUsersPage = () => {
  const { language, user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  // Dialogs
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [resetDialog, setResetDialog] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [detailUser, setDetailUser] = useState(null);
  const [createDialog, setCreateDialog] = useState(false);
  const [createForm, setCreateForm] = useState({ email: '', username: '', password: '', is_admin: false });
  const [editDialog, setEditDialog] = useState(null);
  const [editForm, setEditForm] = useState({ username: '', email: '', preferred_language: 'fr' });

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/admin/users', {
        params: { skip: page * PAGE_SIZE, limit: PAGE_SIZE, search },
      });
      setUsers(res.data.users);
      setTotal(res.data.total);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(0);
    fetchUsers();
  };

  const toggleAdmin = async (userId, currentIsAdmin) => {
    try {
      await axios.patch(`/api/admin/users/${userId}`, { is_admin: !currentIsAdmin });
      toast.success(t('success_saved', language));
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('error_generic', language));
    }
  };

  const toggleActive = async (userId, currentIsActive) => {
    try {
      await axios.patch(`/api/admin/users/${userId}`, { is_active: !currentIsActive });
      toast.success(t('success_saved', language));
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('error_generic', language));
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog) return;
    try {
      await axios.delete(`/api/admin/users/${deleteDialog.id}`);
      toast.success(t('success_deleted', language));
      setDeleteDialog(null);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('error_generic', language));
    }
  };

  const handleResetPassword = async () => {
    if (!resetDialog || !newPassword) return;
    try {
      await axios.post(`/api/admin/users/${resetDialog.id}/reset-password`, {
        new_password: newPassword,
      });
      toast.success(t('success_saved', language));
      setResetDialog(null);
      setNewPassword('');
    } catch (err) {
      toast.error(err.response?.data?.detail || t('error_generic', language));
    }
  };

  const viewDetail = async (userId) => {
    try {
      const res = await axios.get(`/api/admin/users/${userId}`);
      setDetailUser(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleCreateUser = async () => {
    if (!createForm.email || !createForm.username || !createForm.password) return;
    try {
      await axios.post('/api/admin/users', createForm);
      toast.success(t('success_saved', language));
      setCreateDialog(false);
      setCreateForm({ email: '', username: '', password: '', is_admin: false });
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('error_generic', language));
    }
  };

  const handleEditUser = async () => {
    if (!editDialog) return;
    try {
      await axios.patch(`/api/admin/users/${editDialog.id}`, editForm);
      toast.success(t('success_saved', language));
      setEditDialog(null);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('error_generic', language));
    }
  };

  const handleExport = async () => {
    try {
      const res = await axios.get('/api/admin/export/users', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'users_export.csv';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(t('error_generic', language));
    }
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold font-cinzel">{t('admin_user_list', language)}</h1>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={handleExport}>
              <Download className="mr-2 h-4 w-4" />
              {t('admin_export_csv', language)}
            </Button>
            <Button onClick={() => setCreateDialog(true)}>
              <UserPlus className="mr-2 h-4 w-4" />
              {t('admin_user_create', language)}
            </Button>
          </div>
        </div>

        {/* Search */}
        <form onSubmit={handleSearch} className="flex gap-2 max-w-md">
          <Input
            placeholder={t('search', language)}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Button type="submit" size="icon" variant="secondary">
            <Search className="h-4 w-4" />
          </Button>
        </form>

        {/* Users table */}
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/50">
                    <th className="text-left p-3 font-medium">{t('username', language)}</th>
                    <th className="text-left p-3 font-medium">{t('email', language)}</th>
                    <th className="text-left p-3 font-medium hidden md:table-cell">{t('admin_user_campaigns_count', language)}</th>
                    <th className="text-left p-3 font-medium hidden sm:table-cell">{t('admin_status', language)}</th>
                    <th className="text-left p-3 font-medium hidden lg:table-cell">{t('admin_user_last_login', language)}</th>
                    <th className="p-3 w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr
                      key={u.id}
                      className="border-b border-border hover:bg-muted/30 cursor-pointer"
                      onClick={() => viewDetail(u.id)}
                    >
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          {u.username}
                          {u.is_admin && <Badge variant="secondary">Admin</Badge>}
                        </div>
                      </td>
                      <td className="p-3 text-muted-foreground">{u.email}</td>
                      <td className="p-3 hidden md:table-cell">{u.campaigns_count || 0}</td>
                      <td className="p-3 hidden sm:table-cell">
                        <Badge variant={u.is_active !== false ? 'default' : 'destructive'}>
                          {u.is_active !== false ? t('admin_user_status_active', language) : t('admin_user_status_disabled', language)}
                        </Badge>
                      </td>
                      <td className="p-3 text-muted-foreground hidden lg:table-cell">
                        {u.last_login_at ? new Date(u.last_login_at).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US') : '—'}
                      </td>
                      <td className="p-3" onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => {
                              setEditForm({ username: u.username, email: u.email, preferred_language: u.preferred_language || 'fr' });
                              setEditDialog(u);
                            }}>
                              <Pencil className="mr-2 h-4 w-4" />
                              {t('admin_user_edit', language)}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => toggleAdmin(u.id, u.is_admin)}>
                              {u.is_admin ? <ShieldOff className="mr-2 h-4 w-4" /> : <Shield className="mr-2 h-4 w-4" />}
                              {u.is_admin ? t('admin_user_demote', language) : t('admin_user_promote', language)}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => toggleActive(u.id, u.is_active !== false)}>
                              {u.is_active !== false ? <UserX className="mr-2 h-4 w-4" /> : <UserCheck className="mr-2 h-4 w-4" />}
                              {u.is_active !== false ? t('admin_user_deactivate', language) : t('admin_user_activate', language)}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setResetDialog(u)}>
                              <KeyRound className="mr-2 h-4 w-4" />
                              {t('admin_user_reset_password', language)}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              className="text-destructive"
                              disabled={u.id === currentUser?.id}
                              onClick={() => setDeleteDialog(u)}
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              {t('admin_user_delete', language)}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                  {users.length === 0 && !loading && (
                    <tr>
                      <td colSpan={6} className="p-6 text-center text-muted-foreground">
                        {t('no_results', language)}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              {total} {t('admin_count_users', language)}
            </p>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="icon" disabled={page === 0} onClick={() => setPage(page - 1)}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm">
                {page + 1} / {totalPages}
              </span>
              <Button variant="outline" size="icon" disabled={page >= totalPages - 1} onClick={() => setPage(page + 1)}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Delete confirmation dialog */}
      <Dialog open={!!deleteDialog} onOpenChange={() => setDeleteDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('admin_user_delete', language)}</DialogTitle>
            <DialogDescription>{t('admin_user_confirm_delete', language)}</DialogDescription>
          </DialogHeader>
          <p className="text-sm font-medium">{deleteDialog?.username} ({deleteDialog?.email})</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog(null)}>{t('cancel', language)}</Button>
            <Button variant="destructive" onClick={handleDelete}>{t('delete', language)}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset password dialog */}
      <Dialog open={!!resetDialog} onOpenChange={() => { setResetDialog(null); setNewPassword(''); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('admin_user_reset_password', language)}</DialogTitle>
            <DialogDescription>{resetDialog?.username} ({resetDialog?.email})</DialogDescription>
          </DialogHeader>
          <Input
            type="password"
            placeholder={t('admin_new_password', language)}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setResetDialog(null); setNewPassword(''); }}>{t('cancel', language)}</Button>
            <Button onClick={handleResetPassword} disabled={!newPassword}>{t('confirm', language)}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create user dialog */}
      <Dialog open={createDialog} onOpenChange={() => { setCreateDialog(false); setCreateForm({ email: '', username: '', password: '', is_admin: false }); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('admin_user_create', language)}</DialogTitle>
            <DialogDescription>{t('admin_user_create_desc', language)}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>{t('username', language)}</Label>
              <Input
                value={createForm.username}
                onChange={(e) => setCreateForm((f) => ({ ...f, username: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('email', language)}</Label>
              <Input
                type="email"
                value={createForm.email}
                onChange={(e) => setCreateForm((f) => ({ ...f, email: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('admin_new_password', language)}</Label>
              <Input
                type="password"
                value={createForm.password}
                onChange={(e) => setCreateForm((f) => ({ ...f, password: e.target.value }))}
              />
            </div>
            <div className="flex items-center gap-3">
              <Switch
                checked={createForm.is_admin}
                onCheckedChange={(v) => setCreateForm((f) => ({ ...f, is_admin: v }))}
              />
              <Label>Admin</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCreateDialog(false); setCreateForm({ email: '', username: '', password: '', is_admin: false }); }}>
              {t('cancel', language)}
            </Button>
            <Button onClick={handleCreateUser} disabled={!createForm.email || !createForm.username || !createForm.password}>
              {t('confirm', language)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit user dialog */}
      <Dialog open={!!editDialog} onOpenChange={() => setEditDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('admin_user_edit', language)}</DialogTitle>
            <DialogDescription>{editDialog?.email}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>{t('username', language)}</Label>
              <Input
                value={editForm.username}
                onChange={(e) => setEditForm((f) => ({ ...f, username: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('email', language)}</Label>
              <Input
                type="email"
                value={editForm.email}
                onChange={(e) => setEditForm((f) => ({ ...f, email: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('admin_user_language', language)}</Label>
              <select
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm"
                value={editForm.preferred_language}
                onChange={(e) => setEditForm((f) => ({ ...f, preferred_language: e.target.value }))}
              >
                <option value="fr">{t('admin_language_fr', language)}</option>
                <option value="en">{t('admin_language_en', language)}</option>
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog(null)}>{t('cancel', language)}</Button>
            <Button onClick={handleEditUser} disabled={!editForm.username || !editForm.email}>{t('save', language)}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* User detail dialog */}
      <Dialog open={!!detailUser} onOpenChange={() => setDetailUser(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('admin_user_detail', language)}</DialogTitle>
          </DialogHeader>
          {detailUser && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <span className="text-muted-foreground">{t('username', language)}:</span>
                <span className="font-medium">{detailUser.username}</span>
                <span className="text-muted-foreground">{t('email', language)}:</span>
                <span className="font-medium">{detailUser.email}</span>
                <span className="text-muted-foreground">{t('admin_created_at', language)}:</span>
                <span>{new Date(detailUser.created_at).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US')}</span>
                <span className="text-muted-foreground">{t('admin_user_last_login', language)}:</span>
                <span>{detailUser.last_login_at ? new Date(detailUser.last_login_at).toLocaleString(language === 'fr' ? 'fr-FR' : 'en-US') : '—'}</span>
                <span className="text-muted-foreground">Admin:</span>
                <span>{detailUser.is_admin ? t('admin_yes', language) : t('admin_no', language)}</span>
                <span className="text-muted-foreground">{t('admin_user_language', language)}:</span>
                <span>{detailUser.preferred_language === 'fr' ? t('admin_language_fr', language) : t('admin_language_en', language)}</span>
              </div>
              {detailUser.campaigns?.length > 0 && (
                <div>
                  <h3 className="font-semibold mb-2">{t('campaigns', language)} ({detailUser.campaigns.length})</h3>
                  <div className="space-y-1">
                    {detailUser.campaigns.map((c) => (
                      <div key={c.id} className="flex items-center justify-between text-sm p-2 rounded bg-muted/50">
                        <span>{c.name}</span>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline">{c.role}</Badge>
                          {c.is_archived && <Badge variant="secondary">archived</Badge>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminUsersPage;
